"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Switch, Label } from "@/src/components";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsSwitch() {
  const [enabled, setEnabled] = React.useState(false);
  const [notifications, setNotifications] = React.useState(true);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Switch
            </Text>
            <Badge variant="secondary">Form</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A toggle switch component for binary on/off states. Built on Radix UI
            Switch primitive for full accessibility.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/switch" />
        </SectionCard>
      </Section>

      {/* Basic Example */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Switch</CardTitle>
              <CardDescription>Simple toggle without label</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex items-center gap-4">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <Text as="p" className="text-sm text-muted-foreground">
              {enabled ? "On" : "Off"}
            </Text>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`<Switch checked={enabled} onCheckedChange={setEnabled} />`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* With Label */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>With Label</CardTitle>
              <CardDescription>Switch with associated label</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex items-center gap-3">
            <Switch
              id="notifications"
              checked={notifications}
              onCheckedChange={setNotifications}
            />
            <Label htmlFor="notifications">Enable notifications</Label>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`<div className="flex items-center gap-3">
  <Switch id="notifications" checked={checked} onCheckedChange={setChecked} />
  <Label htmlFor="notifications">Enable notifications</Label>
</div>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* States */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>States</CardTitle>
              <CardDescription>Different switch states</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Switch defaultChecked={false} />
              <Text as="p" className="text-sm">Unchecked (default)</Text>
            </div>
            <div className="flex items-center gap-4">
              <Switch defaultChecked={true} />
              <Text as="p" className="text-sm">Checked</Text>
            </div>
            <div className="flex items-center gap-4">
              <Switch disabled />
              <Text as="p" className="text-sm text-muted-foreground">Disabled (unchecked)</Text>
            </div>
            <div className="flex items-center gap-4">
              <Switch disabled defaultChecked />
              <Text as="p" className="text-sm text-muted-foreground">Disabled (checked)</Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Form Example */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Form Integration</CardTitle>
              <CardDescription>Common patterns in forms</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="space-y-6 max-w-md">
            <div className="flex items-center justify-between py-3 border-b">
              <div className="flex flex-col gap-1">
                <Text as="p" className="font-medium">Email notifications</Text>
                <Text as="p" className="text-sm text-muted-foreground">
                  Receive emails about your account activity
                </Text>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <div className="flex flex-col gap-1">
                <Text as="p" className="font-medium">Push notifications</Text>
                <Text as="p" className="text-sm text-muted-foreground">
                  Receive push notifications on your device
                </Text>
              </div>
              <Switch />
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex flex-col gap-1">
                <Text as="p" className="font-medium">Marketing emails</Text>
                <Text as="p" className="text-sm text-muted-foreground">
                  Receive emails about new features and updates
                </Text>
              </div>
              <Switch />
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Default</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">checked</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Controlled checked state</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">defaultChecked</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Default checked state (uncontrolled)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">onCheckedChange</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(checked: boolean) =&gt; void</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Called when checked state changes</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">disabled</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Disable the switch</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">required</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Mark as required for forms</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">name</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Name for form submission</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
          header={<CardTitle>Accessibility</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Built on Radix UI Switch for full WAI-ARIA compliance
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Keyboard: Space to toggle
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Always pair with a Label for screen readers
            </li>
            <li className="flex gap-2">
              <div className="text-primary">-</div>
              Focus ring visible on keyboard navigation
            </li>
          </ul>
        </SectionCard>
      </Section>
    </>
  );
}
